import {
  EsignFieldKind,
  type PublicSignerFieldResponse,
  type PublicSignerSessionResponse,
  type PublicSignerValidationError
} from '../publicSignerTypes';

import {
  getCheckboxFieldValue,
  getRadioGroupFieldValue,
  getSignatureFieldValue,
  getTextFieldValue,
  isFieldEditable
} from './fieldValue';
import type {
  PublicSignerFieldDraftValue,
  PublicSignerFieldDraftValues,
  PublicSignerFieldErrorMap
} from './types';

export function buildFieldDraftValues(
  fields: PublicSignerFieldResponse[]
): PublicSignerFieldDraftValues {
  return Object.fromEntries(
    fields.filter(isFieldEditable).map((field) => {
      if (field.field_kind === EsignFieldKind.Checkbox) {
        return [field.id, getCheckboxFieldValue(field.value)];
      }

      if (field.field_kind === EsignFieldKind.Signature) {
        return [field.id, getSignatureFieldValue(field.value)];
      }

      if (field.field_kind === EsignFieldKind.RadioGroup) {
        return [field.id, getRadioGroupFieldValue(field.value)];
      }

      return [field.id, getTextFieldValue(field.value)];
    })
  );
}

export function mergeFieldDraftValues(
  fields: PublicSignerFieldResponse[],
  previousFieldDraftValues: PublicSignerFieldDraftValues
) {
  const nextFieldDraftValues = buildFieldDraftValues(fields);

  for (const field of fields) {
    if (!isFieldEditable(field) || !(field.id in previousFieldDraftValues)) {
      continue;
    }

    const previousValue = previousFieldDraftValues[field.id];
    if (previousValue !== undefined) {
      nextFieldDraftValues[field.id] = previousValue;
    }
  }

  return nextFieldDraftValues;
}

export function buildFieldErrorMap(
  validationErrors?: PublicSignerValidationError[] | null
) {
  const nextErrors: PublicSignerFieldErrorMap = {};

  for (const validationError of validationErrors ?? []) {
    if (!validationError.field_id || nextErrors[validationError.field_id]) {
      continue;
    }

    nextErrors[validationError.field_id] = validationError.message;
  }

  return nextErrors;
}

export function collectUnboundValidationMessages(
  validationErrors?: PublicSignerValidationError[] | null
): string[] {
  const messages: string[] = [];
  for (const validationError of validationErrors ?? []) {
    if (
      validationError.field_id ||
      validationError.code === 'disclosure_required'
    ) {
      continue;
    }
    if (validationError.message) {
      messages.push(validationError.message);
    }
  }
  return messages;
}

export function filterFieldErrorsForActiveFields(
  fieldErrors: PublicSignerFieldErrorMap,
  fields: PublicSignerFieldResponse[]
) {
  const editableFieldIds = new Set(
    fields.filter(isFieldEditable).map((field) => field.id)
  );

  return Object.fromEntries(
    Object.entries(fieldErrors).filter(([fieldId]) =>
      editableFieldIds.has(fieldId)
    )
  );
}

export function isFieldComplete(
  field: PublicSignerFieldResponse,
  draftValue: PublicSignerFieldDraftValue | undefined
) {
  if (field.field_kind === EsignFieldKind.Checkbox) {
    return !field.is_required || getCheckboxFieldValue(draftValue);
  }

  if (field.field_kind === EsignFieldKind.Signature) {
    return !field.is_required || Boolean(getSignatureFieldValue(draftValue));
  }

  if (field.field_kind === EsignFieldKind.RadioGroup) {
    return !field.is_required || getRadioGroupFieldValue(draftValue) !== null;
  }

  return !field.is_required || Boolean(getTextFieldValue(draftValue).trim());
}

export function getPublicSignerSessionStateKey(
  session: PublicSignerSessionResponse,
  token: string | null | undefined
) {
  return `${session.request_id}:${session.signer_id}:${token ?? ''}`;
}
